// resources/js/admin/hooks/cms/useLocales.ts

import { useState, useEffect, useCallback } from "react";

import useToken from "../auth/useToken";
import { Locale, LocaleFormData } from "../../../types";
import { localeService } from "../../../services/localeService";

export default function useLocales() {
  const { token } = useToken();
  const [locales, setLocales] = useState<Locale[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  });

  // 🔓 PUBLIC - Pas de token nécessaire
  const fetchLocales = useCallback(async (page: number = 1, perPage: number = 20, search?: string, isActive?: boolean, isRtl?: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const response = await localeService.getAll(page, perPage, search, isActive, isRtl);
      setLocales(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des langues");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔓 PUBLIC - Pas de token nécessaire
  const fetchLocaleById = useCallback(async (id: string): Promise<Locale | null> => {
    setLoading(true);
    setError(null);
    try {
      const locale = await localeService.getById(id);
      return locale;
    } catch (err) {
      setError("Erreur lors du chargement de la langue");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔓 PUBLIC - Pas de token nécessaire
  const fetchLocaleByCode = useCallback(async (code: string): Promise<Locale | null> => {
    setLoading(true);
    setError(null);
    try {
      const locale = await localeService.getByCode(code);
      return locale;
    } catch (err) {
      setError("Erreur lors du chargement de la langue par code");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔒 PROTÉGÉ - Token requis
  const createLocale = useCallback(async (data: LocaleFormData): Promise<Locale | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const newLocale = await localeService.create(token, data);
      setLocales((prev) => [newLocale, ...prev]);
      return newLocale;
    } catch (err) {
      setError("Erreur lors de la création de la langue");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const updateLocale = useCallback(async (id: string, data: Partial<LocaleFormData>): Promise<Locale | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const updatedLocale = await localeService.update(token, id, data);
      setLocales((prev) => prev.map((l) => (l.id === id ? updatedLocale : l)));
      return updatedLocale;
    } catch (err) {
      setError("Erreur lors de la mise à jour de la langue");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const deleteLocale = useCallback(async (id: string): Promise<boolean> => {
    if (!token) {
      setError("Vous devez être connecté");
      return false;
    }

    setLoading(true);
    setError(null);
    try {
      await localeService.delete(token, id);
      setLocales((prev) => prev.filter((l) => l.id !== id));
      return true;
    } catch (err) {
      setError("Erreur lors de la suppression de la langue");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const deleteManyLocales = useCallback(async (ids: string[]): Promise<boolean> => {
    if (!token) {
      setError("Vous devez être connecté");
      return false;
    }

    setLoading(true);
    setError(null);
    try {
      await localeService.deleteMany(token, ids);
      setLocales((prev) => prev.filter((l) => !ids.includes(l.id)));
      return true;
    } catch (err) {
      setError("Erreur lors de la suppression multiple");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const toggleActive = useCallback(async (id: string, currentStatus: boolean): Promise<boolean> => {
    if (!token) {
      setError("Vous devez être connecté");
      return false;
    }

    setLoading(true);
    try {
      const updated = await localeService.update(token, id, { is_active: !currentStatus });
      setLocales((prev) => prev.map((l) => (l.id === id ? updated : l)));
      return true;
    } catch (err) {
      setError("Erreur lors du changement de statut");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchLocales();
  }, [fetchLocales]);

  return {
    locales,
    loading,
    error,
    pagination,
    fetchLocales,
    fetchLocaleById,
    fetchLocaleByCode,
    createLocale,
    updateLocale,
    deleteLocale,
    deleteManyLocales,
    toggleActive,
  };
}